import { StarFilled20 } from '@carbon/icons-react';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Task } from 'api/tasks';
import {
  CpBody00Primary,
  CpBody01Primary,
  CpBody01Secondary,
  CpBody02Primary,
} from 'components/atoms/customer-portal/typography';
import { Rate } from 'components/atoms/rate';
import { CpTextArea } from '../cp-input';
import { CpModal } from '../modal/cp-modal';

const Modal = styled(CpModal)`
  .ant-modal-header {
    padding-left: 0;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const CpCsatModalTitleWrapper = styled.div`
  width: calc(100% - 48px);
  display: flex;
  justify-content: start;
  align-items: start;
  flex-direction: column;
  padding-left: var(--cp-spacing-06);
`;

const CpCsatModalBreadcrumbsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const CpCsatBreadcrumbs = styled(CpBody01Secondary)`
  margin-bottom: var(--cp-spacing-02);
`;

const CpCsatRateUrExpTitle = styled(CpBody02Primary)``;

const SatisfactionQuestion = styled(CpBody00Primary)`
  margin-bottom: var(--cp-spacing-06);
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);

  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-03);
    padding-right: var(--cp-spacing-03);
  }
`;

const RateWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);

  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-03);
    padding-right: var(--cp-spacing-03);
  }
`;

const StarBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 80px;
  background-color: var(--cp-coolest-gray-10);
`;

const RatingLabel = styled(CpBody01Primary)`
  font-weight: bold;
  margin-top: var(--cp-spacing-05);
  width: 100%;
  text-align: center;
`;

const MessageWrapper = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  flex-direction: column;
  padding: var(--cp-spacing-06);

  @media (max-width: 480px) {
    padding: var(--cp-spacing-03);
  }

  textarea {
    width: 100%;
  }
`;

const MessageLabel = styled(CpBody00Primary)`
  margin-bottom: var(--cp-spacing-03);
  width: 100%;
  text-align: start;
`;

export const CpCsatModal: React.FC<{
  csat: Task | null;
  visible?: boolean;
  onSubmit: (args: { rating: number; message: string }) => void;
  onCancel: VoidFunction;
}> = ({ csat, visible, onCancel, onSubmit }) => {
  const [rating, setRating] = React.useState<number>(1);
  const ratingRef = React.useRef<number>(rating);
  const [message, setMessage] = React.useState<string>('');

  const onChange = useCallback((value: number): void => {
    ratingRef.current = value;
    setRating(value);
  }, []);

  const onHoverChange = useCallback(
    (value: number): void => {
      ratingRef.current = rating;
      queueMicrotask(() => {
        setRating(value);
      });
    },
    [rating],
  );

  const onMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
    },
    [],
  );

  const _onSubmit = useCallback(() => {
    onSubmit({ rating: ratingRef.current, message });
    setMessage('');
    setRating(1);
  }, [ratingRef, message, onSubmit]);

  const _onCancel = useCallback(() => {
    onCancel();
    setMessage('');
    setRating(1);
  }, [onCancel]);

  return (
    <Modal
      getContainer={() =>
        document.getElementById('cp-modal-root') as HTMLElement
      }
      okText={<FormattedMessage id="done" />}
      cancelText={<FormattedMessage id="cancel" />}
      title={
        csat ? (
          <CpCsatModalTitleWrapper>
            <CpCsatModalBreadcrumbsWrapper>
              <CpCsatBreadcrumbs>
                {[
                  csat.project?.projectName,
                  csat.parentTaskObject?.taskName,
                  csat.taskName,
                ]
                  .filter(Boolean)
                  .join(' > ')}
              </CpCsatBreadcrumbs>
            </CpCsatModalBreadcrumbsWrapper>
            <CpCsatRateUrExpTitle>
              <FormattedMessage id="rate_your_experience" />
            </CpCsatRateUrExpTitle>
          </CpCsatModalTitleWrapper>
        ) : (
          <></>
        )
      }
      visible={visible}
      onCancel={_onCancel}
      onOk={_onSubmit}
    >
      <SatisfactionQuestion>
        <FormattedMessage
          id="cp.csat.question"
          values={{
            name: csat?.taskName,
            strong: (chunks) => {
              return <strong>{chunks}</strong>;
            },
          }}
        />
      </SatisfactionQuestion>
      <RateWrapper>
        <Rate
          count={5}
          defaultValue={0}
          onChange={onChange}
          value={rating}
          allowHalf={false}
          allowClear={false}
          onHoverChange={onHoverChange}
          character={
            <StarBox className="star-box">
              <StarFilled20 />
            </StarBox>
          }
        />
        <RatingLabel>
          {rating ? <FormattedMessage id={`csat.rating.${rating}`} /> : <></>}
        </RatingLabel>
      </RateWrapper>
      <MessageWrapper>
        <MessageLabel>
          <FormattedMessage id="cp.csat.message.label" />
        </MessageLabel>
        <CpTextArea value={message} onChange={onMessageChange} />
      </MessageWrapper>
    </Modal>
  );
};
